/**
 * Push Token Service
 * Registers the device's FCM token in Supabase so server-side cron jobs
 * (e.g. the validation reminder) can deliver push notifications.
 *
 * Native only (iOS/Android). On web/dev it no-ops cleanly.
 */
import { Capacitor } from '@capacitor/core'
import { FirebaseMessaging } from '@capacitor-firebase/messaging'
import type { PluginListenerHandle } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { supabase } from '@/lib/supabase'
import { i18n } from '@/i18n'
import { useAnalytics } from '@/composables/useAnalytics'

const analytics = useAnalytics()

type Platform = 'ios' | 'android' | 'web'

// Device-local reminder preference. These keys are OWNED by useValidationReminder
// (its source of truth); mirrored here so registration seeds the server row.
// Kept as literals to avoid a circular import (the composable imports this file).
const REMINDER_ENABLED_KEY = 'validationReminder.enabled'
const REMINDER_TIME_KEY = 'validationReminder.time'
const REMINDER_DEFAULT_ENABLED = true
const REMINDER_DEFAULT_TIME = '20:00'

// Préférence locale du push « une tâche vient d'être envoyée ». Comme le rappel,
// l'appareil en est la source de vérité et la ligne serveur en est le reflet.
const SUBMISSION_PUSH_KEY = 'submissionPush.enabled'
const SUBMISSION_PUSH_DEFAULT = true

/** Le parent veut-il être prévenu dès qu'un enfant envoie une tâche ? */
export async function getSubmissionPushEnabled(): Promise<boolean> {
  try {
    const { value } = await Preferences.get({ key: SUBMISSION_PUSH_KEY })
    return value === null ? SUBMISSION_PUSH_DEFAULT : value === 'true'
  } catch {
    return SUBMISSION_PUSH_DEFAULT
  }
}

/** Retient le choix sur l'appareil et le recopie sur le jeton de cet appareil. */
export async function setSubmissionPushEnabled(enabled: boolean): Promise<void> {
  try { await Preferences.set({ key: SUBMISSION_PUSH_KEY, value: String(enabled) }) } catch { /* rien à retenir */ }
  if (!Capacitor.isNativePlatform()) return
  let token: string | undefined
  try {
    token = (await FirebaseMessaging.getToken()).token
  } catch {
    return
  }
  if (!token) return
  const { error } = await supabase
    .from('device_push_tokens')
    .update({ submission_push_enabled: enabled } as never)
    .eq('token', token)
  if (error) console.error('[push] sync submission push preference failed', error)
  lastUpsertedSignature = null
}

async function localReminderPreference(): Promise<{ enabled: boolean; time: string }> {
  try {
    const [{ value: rawEnabled }, { value: rawTime }] = await Promise.all([
      Preferences.get({ key: REMINDER_ENABLED_KEY }),
      Preferences.get({ key: REMINDER_TIME_KEY }),
    ])
    const validTime = !!rawTime && /^([01]\d|2[0-3]):[0-5]\d$/.test(rawTime)
    return {
      enabled: rawEnabled === null ? REMINDER_DEFAULT_ENABLED : rawEnabled === 'true',
      time: validTime ? (rawTime as string) : REMINDER_DEFAULT_TIME,
    }
  } catch {
    return { enabled: REMINDER_DEFAULT_ENABLED, time: REMINDER_DEFAULT_TIME }
  }
}

function currentPlatform(): Platform {
  const p = Capacitor.getPlatform()
  return p === 'ios' || p === 'android' ? p : 'web'
}

function currentLocale(): string {
  const raw = (i18n.global.locale as unknown as { value: string }).value || 'en'
  // Store the base language only (fr, en, pt-BR…) — edge function matches on it.
  return raw
}

function deviceTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return undefined
  }
}

let tokenListener: PluginListenerHandle | null = null

/**
 * True if the OS already granted notification permission.
 * Does NOT prompt — use requestPermission() for that (gated by a pre-prompt).
 */
export async function hasNotificationPermission(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false
  try {
    const { receive } = await FirebaseMessaging.checkPermissions()
    return receive === 'granted'
  } catch {
    return false
  }
}

export type NotificationPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported'

/**
 * Où en est l'autorisation système, sans rien demander. `prompt` = la demande
 * n'a jamais été posée sur cet appareil : c'est le seul état où l'app peut
 * encore l'ouvrir. `denied` ne se répare que dans les réglages du système.
 */
export async function getNotificationPermissionState(): Promise<NotificationPermissionState> {
  if (!Capacitor.isNativePlatform()) return 'unsupported'
  try {
    const { receive } = await FirebaseMessaging.checkPermissions()
    if (receive === 'granted' || receive === 'denied') return receive
    return 'prompt'
  } catch {
    return 'unsupported'
  }
}

/**
 * Trigger the OS permission prompt. One-shot on iOS — caller must gate this
 * behind a custom pre-prompt and never call it cold.
 * Returns true if granted.
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false
  try {
    const { receive } = await FirebaseMessaging.requestPermissions()
    // Taux d'opt-in push : inconnu jusqu'ici alors que les rappels portent une
    // part de la rétention. Émis uniquement sur l'octroi, pas sur l'affichage
    // de la demande — c'est l'accord qui change ce qu'on peut faire.
    if (receive === 'granted') void analytics.logNotificationEnabled('push')
    return receive === 'granted'
  } catch (err) {
    console.error('[push] requestPermissions failed', err)
    return false
  }
}

/**
 * Fetch the current FCM token and upsert it for the signed-in user.
 * Safe to call repeatedly (idempotent upsert on the token).
 */
export async function registerCurrentToken(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  if (!(await hasNotificationPermission())) return

  const { data: sessionData } = await supabase.auth.getSession()
  const userId = sessionData.session?.user?.id
  if (!userId) return

  let token: string | undefined
  try {
    const res = await FirebaseMessaging.getToken()
    token = res.token
  } catch (err) {
    console.error('[push] getToken failed', err)
    return
  }
  if (!token) return

  await upsertToken(userId, token)
}

let lastUpsertedSignature: string | null = null

/**
 * L'enfant sur lequel l'appareil est verrouillé, ou null. Un appareil en mode
 * enfant verrouillé (téléphone appairé, tablette partagée) est dans les mains
 * d'un enfant : son jeton est marqué, et les envois destinés aux parents
 * l'ignorent (#402). Lu dans le stockage du verrou (appContext) plutôt que dans
 * le store, que ce service ne peut pas importer sans cycle.
 */
function lockedChildId(): string | null {
  try {
    if (localStorage.getItem('harmonia_child_view_locked') !== '1') return null
    return localStorage.getItem('harmonia_child_view_child_id')
  } catch {
    return null
  }
}

async function upsertToken(userId: string, token: string): Promise<void> {
  // Seed the reminder preference from the device (its source of truth) so a
  // token RECLAIMED from a previous account on this device doesn't inherit that
  // account's stale reminder_enabled — otherwise the cron stays silent even
  // after ownership moves. Fresh inserts would default to enabled, but the
  // onConflict UPDATE path keeps the old row's value unless we set it here.
  const reminder = await localReminderPreference()
  const row = {
    user_id: userId,
    token,
    platform: currentPlatform(),
    locale: currentLocale(),
    timezone: deviceTimezone(),
    reminder_enabled: reminder.enabled,
    reminder_time: reminder.time,
    submission_push_enabled: await getSubmissionPushEnabled(),
    child_mode_child_id: lockedChildId(),
  }
  // La même ligne a déjà été écrite pendant cette session : rien à redire au
  // serveur. Depuis que chaque validation par carte repasse par ici
  // (HARMONIA-80), l'upsert partait à chaque tâche cochée (HARMONIA-77).
  const signature = JSON.stringify(row)
  if (signature === lastUpsertedSignature) return
  let { error } = await supabase
    .from('device_push_tokens')
    .upsert(row as never, { onConflict: 'token' })
  // Base pas encore migrée (colonne inconnue) : le jeton s'inscrit quand même,
  // sans la marque. Perdre les notifications serait pire.
  if (error?.code === 'PGRST204') {
    const { child_mode_child_id: _unused, ...legacyRow } = row
    void _unused
    ;({ error } = await supabase
      .from('device_push_tokens')
      .upsert(legacyRow as never, { onConflict: 'token' }))
  }
  if (error) {
    console.error('[push] upsert token failed', error)
    return
  }
  lastUpsertedSignature = signature
}

/**
 * Push only the device-local reminder preference to the server row for this
 * device, so the cron knows whether/when to send. No-op if no token registered.
 */
export async function syncReminderPreference(opts: {
  enabled: boolean
  time: string
}): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  const { data: sessionData } = await supabase.auth.getSession()
  const userId = sessionData.session?.user?.id
  if (!userId) return

  let token: string | undefined
  try {
    token = (await FirebaseMessaging.getToken()).token
  } catch {
    return
  }
  if (!token) return

  const { error } = await supabase
    .from('device_push_tokens')
    .update({
      reminder_enabled: opts.enabled,
      reminder_time: opts.time,
      timezone: deviceTimezone(),
    })
    .eq('token', token)
  if (error) console.error('[push] sync reminder preference failed', error)
  lastUpsertedSignature = null
}

let tapListener: PluginListenerHandle | null = null

/**
 * Handle taps on a delivered notification. The reminder carries
 * data.route = '/tabs/tasks'; we hand it to the caller (App.vue) to
 * navigate. Idempotent — safe to call once at app startup.
 */
export async function setupNotificationTapHandler(
  navigate: (route: string) => void,
): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  if (tapListener) return
  tapListener = await FirebaseMessaging.addListener(
    'notificationActionPerformed',
    (event) => {
      const route = (event.notification?.data as Record<string, string> | undefined)?.route
      if (route) navigate(route)
    },
  )
}

/**
 * Start listening for FCM token refreshes (FCM rotates tokens). Call once after
 * login on a permitted device.
 */
export async function startTokenRefreshListener(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  if (tokenListener) return
  tokenListener = await FirebaseMessaging.addListener('tokenReceived', (event) => {
    void (async () => {
      const { data } = await supabase.auth.getSession()
      const userId = data.session?.user?.id
      if (userId && event.token) await upsertToken(userId, event.token)
    })()
  })
}

/**
 * Remove the current device token from the server (call on logout, while the
 * session is still valid for RLS). Also tears down listeners.
 */
// `FirebaseMessaging.getToken()` attend le jeton APNs d'Apple. Sur un
// simulateur iOS (et sur un appareil qui n'en obtient pas), il ne vient jamais
// et la promesse ne se résout JAMAIS. Attendu dans la déconnexion, il la
// bloquait tout entière : « Retour » depuis le premier écran de l'onboarding
// n'atteignait jamais /welcome, la page restait figée. On lâche au bout de
// 3 s : sans jeton connu, il n'y a de toute façon rien à désenregistrer.
const GET_TOKEN_TIMEOUT_MS = 3000

function tokenWithin(ms: number): Promise<string | null> {
  return Promise.race([
    FirebaseMessaging.getToken().then(r => r.token ?? null),
    new Promise<null>(resolve => setTimeout(() => resolve(null), ms)),
  ])
}

export async function unregisterCurrentToken(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return
  try {
    const token = await tokenWithin(GET_TOKEN_TIMEOUT_MS)
    if (token) {
      await supabase.from('device_push_tokens').delete().eq('token', token)
    }
    lastUpsertedSignature = null
  } catch (err) {
    console.error('[push] unregister failed', err)
  } finally {
    if (tokenListener) {
      await tokenListener.remove()
      tokenListener = null
    }
  }
}
